import React from 'react';
import type { AcademyOperatingProfileInput } from '@court-sync/shared';
import {
  ACADEMY_SIZE_BANDS,
  COACH_COUNT_BANDS,
  LOCATION_COUNT_BANDS,
  OPERATING_ROLES,
} from '@court-sync/shared';
import { DashboardDataCard } from '@/components/dashboard/DashboardSectionCard';
import { cn } from '@/lib/utils';
import {
  ONBOARDING_WORKFLOW_LABELS,
} from '@/utils/planRecommendationUi';

const ROLE_LABELS: Record<(typeof OPERATING_ROLES)[number], string> = {
  solo_coach: 'Solo coach',
  owner_coach: 'Owner who also coaches',
  owner_manager: 'Owner / manager',
};

const SIZE_LABELS: Record<(typeof ACADEMY_SIZE_BANDS)[number], string> = {
  '1-25': '1–25 students',
  '26-100': '26–100 students',
  '101-300': '101–300 students',
  '300+': '300+ students',
};

const COACH_BAND_LABELS: Record<(typeof COACH_COUNT_BANDS)[number], string> = {
  '0': '0 coaches',
  '1': '1 coach',
  '2-5': '2–5 coaches',
  '6+': '6+ coaches',
};

const LOCATION_BAND_LABELS: Record<(typeof LOCATION_COUNT_BANDS)[number], string> = {
  '1': '1 location',
  '2-3': '2–3 locations',
  '4+': '4+ locations',
};

export interface OperatingProfileSectionProps {
  operatingProfile?: AcademyOperatingProfileInput | null;
  derivedCounts: {
    coaches: number;
    locations: number;
    courts: number;
    activeSports: number;
  };
  title?: string;
  description?: string;
  className?: string;
}

function ProfileRow({
  label,
  declared,
  current,
  highlightMismatch,
}: {
  label: string;
  declared?: string | null;
  current?: string | number | null;
  highlightMismatch?: boolean;
}) {
  return (
    <tr className="border-b border-border last:border-0">
      <th
        scope="row"
        className="w-[38%] py-3 pr-4 text-left text-sm font-medium text-foreground align-top sm:w-auto sm:whitespace-nowrap"
      >
        {label}
      </th>
      <td className="w-[31%] py-3 px-3 text-sm text-muted-foreground align-top sm:w-[34%]">
        {declared ?? '—'}
      </td>
      <td
        className={cn(
          'w-[31%] py-3 pl-3 text-sm text-right tabular-nums align-top sm:w-[34%]',
          highlightMismatch ? 'font-medium text-amber-700 dark:text-amber-300' : 'text-foreground'
        )}
      >
        {current ?? '—'}
      </td>
    </tr>
  );
}

function declaredCoachBandMatches(derived: number, band?: string | null): boolean {
  if (!band) return true;
  if (band === '0') return derived === 0;
  if (band === '1') return derived === 1;
  if (band === '2-5') return derived >= 2 && derived <= 5;
  if (band === '6+') return derived >= 6;
  return true;
}

function declaredLocationBandMatches(derived: number, band?: string | null): boolean {
  if (!band) return true;
  if (band === '1') return derived <= 1;
  if (band === '2-3') return derived >= 2 && derived <= 3;
  if (band === '4+') return derived >= 4;
  return true;
}

function formatDeclaredList(
  values: string[] | undefined,
  labels: Record<string, string>
): string | null {
  if (!values?.length) return null;
  return values.map((value) => labels[value] ?? value).join(', ');
}

export function OperatingProfileSection({
  operatingProfile,
  derivedCounts,
  title = 'Operating profile',
  description = 'Declared onboarding answers vs live counts. Used for checklist order and recommendations only — never blocks features.',
  className,
}: OperatingProfileSectionProps) {
  const profile = operatingProfile;

  return (
    <DashboardDataCard
      title={title}
      description={description}
      className={className}
      contentClassName="px-4 py-5 sm:px-6"
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[20rem] table-fixed">
          <thead>
            <tr className="border-b border-border text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <th className="pb-3 pr-4 text-left">Metric</th>
              <th className="w-[34%] pb-3 px-3 text-left">Declared</th>
              <th className="w-[34%] pb-3 pl-3 text-right">Current</th>
            </tr>
          </thead>
          <tbody>
            <ProfileRow
              label="Operating role"
              declared={
                profile?.operatingRole ? ROLE_LABELS[profile.operatingRole] : 'Not set'
              }
            />
            <ProfileRow
              label="Size band"
              declared={profile?.sizeBand ? SIZE_LABELS[profile.sizeBand] : 'Not set'}
            />
            <ProfileRow
              label="Coaches"
              declared={
                profile?.operatingRole === 'solo_coach' || profile?.coachCountBand === '0'
                  ? 'Solo — just me'
                  : profile?.coachesNeedPortalAccess === true
                    ? 'Coach logins'
                    : profile?.coachesNeedPortalAccess === false
                      ? 'Basic info only'
                      : profile?.coachCountBand
                        ? COACH_BAND_LABELS[profile.coachCountBand]
                        : null
              }
              current={derivedCounts.coaches}
              highlightMismatch={
                profile?.coachCountBand
                  ? !declaredCoachBandMatches(derivedCounts.coaches, profile.coachCountBand)
                  : false
              }
            />
            <ProfileRow
              label="Locations"
              declared={
                profile?.locationCountBand
                  ? LOCATION_BAND_LABELS[profile.locationCountBand]
                  : null
              }
              current={derivedCounts.locations}
              highlightMismatch={
                !declaredLocationBandMatches(derivedCounts.locations, profile?.locationCountBand)
              }
            />
            <ProfileRow label="Courts" current={derivedCounts.courts} />
            <ProfileRow label="Active sports" current={derivedCounts.activeSports} />
            <ProfileRow
              label="Programs & operations"
              declared={formatDeclaredList(profile?.desiredWorkflows, ONBOARDING_WORKFLOW_LABELS)}
            />
          </tbody>
        </table>
      </div>
    </DashboardDataCard>
  );
}
